import { useParams } from 'react-router-dom';
import { getReplay, listReplays } from '../../shared/api/client';
import { replayFromApi } from '../../shared/api/transform';
import { InputHistogram, SpeedChart, SteerChart, SyncContext } from '../../shared/design/Charts';
import { Icon } from '../../shared/design/Icons';
import { Minimap } from '../../shared/design/Minimap';
import { EmptyState, Stat, TierBadge, TMLogo } from '../../shared/design/Primitives';
import { Scrubber } from '../../shared/design/Scrubber';
import { useAsync } from '../../shared/hooks/useAsync';
import { formatTime, Replay, sampleAt } from '../../shared/model/Replay';
import { LiveReadout } from '../scan/ScanPage';

export function ScanHeroPage() {
  const { uid } = useParams<{ uid: string }>();

  // No uid → grab the most-clustered (most "F-tier-like") replay so the hero
  // shows off the action-key story. Falls back to the first replay if nobody's
  // suspicious yet.
  const state = useAsync<Replay | null>(async () => {
    if (uid) return replayFromApi(await getReplay(uid));
    const summaries = await listReplays(20);
    if (summaries.length === 0) return null;
    const pick = [...summaries].sort((a, b) => b.clusterCount - a.clusterCount)[0];
    return replayFromApi(await getReplay(pick.ghostUid));
  }, [uid]);

  if (state.loading) {
    return (
      <div
        className="tm"
        style={{
          minHeight: '100vh',
          background: 'var(--bg-page)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--fg-3)',
        }}
      >
        Loading hero…
      </div>
    );
  }

  if (state.error || !state.data) {
    return (
      <div className="tm" style={{ minHeight: '100vh', background: 'var(--bg-page)', padding: 60 }}>
        <EmptyState
          title="No replay to show"
          body={state.error?.message ?? 'Scan at least one replay first.'}
          icon={<Icon.Warn />}
          accent="var(--bad)"
        />
      </div>
    );
  }

  return <HeroFrame replay={state.data} />;
}

function HeroFrame({ replay }: { replay: Replay }) {
  const scrubT = 0.32;
  const cur = sampleAt(replay, scrubT);
  const isBad = replay.data.clusters.length > 1;

  return (
    <SyncContext.Provider value={{ scrubT, hoverT: null, setHoverT: () => {} }}>
      <div
        className="tm"
        style={{
          minHeight: '100vh',
          background: 'var(--bg-page)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <div
          style={{
            height: 56,
            background: 'var(--bg-header)',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            padding: '0 24px',
            gap: 24,
            flexShrink: 0,
          }}
        >
          <TMLogo size={26} />
          <div style={{ width: 1, height: 22, background: 'var(--border)' }} />
          <div style={{ display: 'flex', gap: 4 }}>
            {['Home', 'Scan', 'Compare', 'Leaderboard', 'About'].map((l) => (
              <span
                key={l}
                style={{
                  padding: '6px 12px',
                  color: l === 'Scan' ? 'var(--accent)' : 'var(--fg-3)',
                  fontSize: 13,
                  fontWeight: l === 'Scan' ? 600 : 500,
                  borderRadius: 4,
                  background: l === 'Scan' ? 'var(--accent-12)' : 'transparent',
                }}
              >
                {l}
              </span>
            ))}
          </div>
          <div style={{ flex: 1 }} />
          <div
            style={{
              height: 36,
              padding: '0 14px',
              background: 'var(--bg-surface-2)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--r-6)',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              minWidth: 360,
              maxWidth: 480,
            }}
          >
            <Icon.Search style={{ color: 'var(--fg-3)' }} />
            <span
              style={{
                fontSize: 12,
                color: 'var(--fg-muted)',
                fontFamily: 'var(--font-mono)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              trackmania.io/replay/{replay.meta.ghostUid}
            </span>
          </div>
        </div>

        <div
          style={{
            flex: 1,
            display: 'grid',
            gridTemplateColumns: '1.45fr 1fr',
            gap: 18,
            padding: 24,
            minHeight: 0,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minHeight: 0 }}>
            <div
              style={{
                background: 'var(--bg-surface)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--r-8)',
                padding: '14px 18px',
                display: 'flex',
                alignItems: 'center',
                gap: 18,
              }}
            >
              <TierBadge tier={replay.meta.tier} size="xl" />
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                  <span className="eyebrow">{replay.meta.location || '—'}</span>
                  <span style={{ color: 'var(--fg-dim)' }}>·</span>
                  <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--fg-3)' }}>
                    {replay.meta.ghostUid}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
                  <h1 style={{ fontSize: 26, fontWeight: 600, color: 'var(--fg-1)', margin: 0 }}>{replay.meta.author}</h1>
                  <span style={{ fontSize: 18, color: 'var(--fg-3)' }}>on</span>
                  <span style={{ fontSize: 18, color: 'var(--fg-1)' }}>{replay.meta.map}</span>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div className="eyebrow">Finish</div>
                <div
                  className="num"
                  style={{ fontSize: 32, fontWeight: 600, color: 'var(--fg-1)', letterSpacing: '-0.02em' }}
                >
                  {replay.meta.raceTimeStr}
                </div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 14, flex: 1, minHeight: 0 }}>
              <div
                style={{
                  background: 'var(--bg-surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--r-8)',
                  padding: 14,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                  minHeight: 0,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div className="eyebrow">Track · color-graded by speed</div>
                  <div style={{ flex: 1 }} />
                  <div
                    style={{
                      display: 'flex',
                      gap: 0,
                      background: 'var(--bg-surface-2)',
                      padding: 3,
                      borderRadius: 'var(--r-6)',
                    }}
                  >
                    {['speed', 'gear', 'steer'].map((o) => (
                      <span
                        key={o}
                        style={{
                          padding: '4px 10px',
                          background: o === 'speed' ? 'var(--bg-surface-3)' : 'transparent',
                          color: o === 'speed' ? 'var(--fg-1)' : 'var(--fg-3)',
                          borderRadius: 4,
                          fontFamily: 'var(--font-mono)',
                          fontSize: 11,
                          fontWeight: 500,
                          textTransform: 'uppercase',
                        }}
                      >
                        {o}
                      </span>
                    ))}
                  </div>
                </div>
                <div style={{ flex: 1, minHeight: 0 }}>
                  <Minimap replay={replay} width={620} height={430} scrubT={scrubT} overlay="speed" showLabels />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 2 }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--fg-muted)' }}>0</span>
                  <div
                    style={{
                      flex: 1,
                      height: 6,
                      borderRadius: 3,
                      background:
                        'linear-gradient(to right, #1a4e8a, #0099ff, #00d4c4, #7bf06b, #ffe14a, #ff8a3c, #ff3355)',
                    }}
                  />
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--fg-muted)' }}>
                    {replay.meta.topSpeedKmh} km/h
                  </span>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minHeight: 0 }}>
                <LiveReadout cur={cur} />
                <div
                  style={{
                    background: 'var(--bg-surface)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--r-8)',
                    padding: '14px 18px',
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 12,
                  }}
                >
                  <Stat label="Respawns" value={replay.meta.respawns} />
                  <Stat label="Top speed" value={replay.meta.topSpeedKmh} unit="km/h" />
                  <Stat label="Avg speed" value={replay.meta.avgSpeedKmh} unit="km/h" />
                  <Stat
                    label="Sus. clusters"
                    value={replay.data.clusters.length}
                    accent={replay.data.clusters.length > 0 ? 'var(--bad)' : 'var(--ok)'}
                  />
                </div>
              </div>
            </div>

            <Scrubber
              replay={replay}
              scrubT={scrubT}
              onChange={() => {}}
              playing={false}
              setPlaying={() => {}}
              speed={1}
              setSpeed={() => {}}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minHeight: 0 }}>
            <CompactVerdict replay={replay} isBad={isBad} />
            <SteerChart replay={replay} height={120} />
            <SpeedChart replay={replay} height={100} />
            <InputHistogram replay={replay} height={200} />
          </div>
        </div>
      </div>
    </SyncContext.Provider>
  );
}

function CompactVerdict({ replay, isBad }: { replay: Replay; isBad: boolean }) {
  const data = replay.data;
  const topCluster = [...data.clusters].sort((a, b) => b.count - a.count)[0];
  const borderColor = isBad ? 'var(--bad)' : 'var(--ok)';
  const textColor = isBad ? 'var(--bad)' : 'var(--ok)';

  return (
    <div
      style={{
        background: 'var(--bg-surface)',
        border: `1.5px solid ${borderColor}`,
        borderRadius: 'var(--r-8)',
        padding: 18,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: 180,
          height: 180,
          background: isBad
            ? 'radial-gradient(circle at top right, rgba(255,51,85,0.18), transparent 70%)'
            : 'radial-gradient(circle at top right, rgba(0,255,136,0.14), transparent 70%)',
          pointerEvents: 'none',
        }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, position: 'relative' }}>
        <span style={{ color: textColor }}>
          {isBad ? <Icon.Warn /> : <Icon.Check />}
        </span>
        <h2 style={{ fontSize: 18, fontWeight: 600, color: textColor, margin: 0 }}>
          {isBad ? 'Action-key input detected' : 'Clean analog input'}
        </h2>
        <div style={{ flex: 1 }} />
        <TierBadge tier={replay.meta.tier} size="lg" />
      </div>
      {isBad && topCluster ? (
        <p style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--fg-2)', margin: 0, position: 'relative' }}>
          We saw{' '}
          <span className="mono" style={{ color: 'var(--bad)', fontWeight: 600 }}>
            {topCluster.count}
          </span>{' '}
          identical steering inputs of value{' '}
          <span className="mono" style={{ color: 'var(--bad)', fontWeight: 600 }}>
            {topCluster.value > 0 ? '+' : ''}
            {topCluster.value.toFixed(3)}
          </span>{' '}
          between{' '}
          <span className="mono" style={{ color: 'var(--fg-1)' }}>
            {formatTime(topCluster.tStart * 1000)}
          </span>{' '}
          and{' '}
          <span className="mono" style={{ color: 'var(--fg-1)' }}>
            {formatTime(topCluster.tEnd * 1000)}
          </span>
          . That's not analog stick behaviour.
        </p>
      ) : (
        <p style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--fg-2)', margin: 0, position: 'relative' }}>
          No steering inputs repeat exactly for &gt; 8 consecutive ticks — distribution is continuous, consistent with an analog
          stick or wheel.
        </p>
      )}
      <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 10, position: 'relative' }}>
        <span className="eyebrow">Confidence</span>
        <div
          style={{ flex: 1, height: 6, background: 'var(--bg-surface-2)', borderRadius: 3, overflow: 'hidden' }}
        >
          <div style={{ height: '100%', width: isBad ? '92%' : '88%', background: borderColor }} />
        </div>
        <span className="num" style={{ fontSize: 13, color: textColor, fontWeight: 600 }}>
          {isBad ? '92%' : '88%'}
        </span>
      </div>
    </div>
  );
}
